import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { capitalize, underscored } from 'underscore.string';
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
} from 'draft-js';

import WYSIWYG from '../../../../components/Input/WYSIWYG';
import LayoutContext from '../../../../store/Layout/contexts';
import Upload from '../../../../components/Input/Upload';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';
import AlertService from '../../../../components/SweetAlert';
import { newFetch, GET, POST, DELETE } from '../../../../helpers/fetch';

import { config } from '../../../../config';
const { SERVER_URL } = config;

const EditItem = ({
  history,
  match: {
    params: { item_id },
  },
}) => {
  const isJSON = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const createFileFromURL = async (url, fileName) => {
    let response = await fetch(url);
    let data = await response.blob();
    let metadata = {
      type: 'image/jpeg',
    };

    return new File([data], `${fileName}.jpg`, metadata);
  };

  const [item, setItem] = useState(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState(EditorState.createEmpty());
  const types = ['free', 'redeemable'];
  const [type, setType] = useState('redeemable');
  const [threshold, setThreshold] = useState(null);
  const [points, setPoints] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [mailingPoints, setMailingPoints] = useState(0);
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [collectionPoint, setCollectionPoint] = useState('');
  const [remarks, setRemarks] = useState('');
  const [active, setActive] = useState(true);

  const fetchItem = async () => {
    let api_path = `/reward/${item_id}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      let itemData = response.results;

      setItem(itemData);
      setName(itemData.name);
      setDescription(
        itemData.description
          ? isJSON(itemData.description)
            ? EditorState.createWithContent(
                convertFromRaw(JSON.parse(itemData.description))
              )
            : EditorState.createWithContent(
                ContentState.createFromText(itemData.description)
              )
          : EditorState.createEmpty()
      );

      setType(itemData.type);
      setThreshold(itemData.free_threshold);
      setPoints(itemData.redeem_points);
      setQuantity(itemData.quantity);
      setMailingPoints(itemData.mailing_cost);
      setImages(itemData.images);
      setActive(itemData.active);

      // if (itemData.images.length > 0) {
      //   let imagesArr = await Promise.all(
      //     itemData.images.map(
      //       async (imageUrl, index) =>
      //         await createFileFromURL(
      //           imageUrl,
      //           underscored(`${itemData.name}_${index}`)
      //         )
      //     )
      //   )

      //   setImages(imagesArr)
      // }

      setInstruction(itemData.instructions);
      setCollectionPoint(itemData.collection_point);
      setRemarks(itemData.remarks);
    }
  };

  const convertUrlToFile = async () => {
    let imagesArr = await Promise.all(
      item.images.map(
        async (imageUrl, index) =>
          await createFileFromURL(
            imageUrl,
            underscored(`${item.name}_${index}`)
          )
      )
    );

    setImages(imagesArr);
  };

  useEffect(() => {
    fetchItem();
  }, []);

  // useEffect(() => {
  //   if (images.length > 0) {
  //     convertUrlToFile()
  //   }
  // }, [images])

  const handleDiscard = async () => {
    if (
      await AlertService.confirm(
        '',
        'Are you sure you want to discard all changes to the item?'
      )
    ) {
      setName(item.name);
      setDescription(item.description);
      setType(item.type);
      setThreshold(item.threshold);
      setPoints(item.redeem_points);
      setQuantity(item.quantity);
      setMailingPoints(item.mailing_cost);
      setInstruction(item.instructions);
      setCollectionPoint(item.collection_point);
      setRemarks(item.remarks);
    }
  };

  const handleDelete = async () => {
    if (item.redeemed.length > 0) {
      ToastUtil.error(
        'Item is unable to delete because associate has redeemed this item.'
      );
    } else {
      if (
        await AlertService.confirm(
          '',
          'Are you sure you want to delete this item?'
        )
      ) {
        let api_path = `/reward/${item_id}`;
        let response = await newFetch(DELETE, api_path);

        if (response.success) {
          ToastUtil.success(response.message);
          history.push('/rewards');
        }
      }
    }
  };

  const handleDeleteImage = async (image_id) => {
    if (
      await AlertService.confirm(
        '',
        'Are you sure you want to delete this image?'
      )
    ) {
      let api_path = `/reward/image/${image_id}`;
      let response = await newFetch(DELETE, api_path);

      ToastUtil.success(response.message);
      response.success && fetchItem();
    }
  };

  const handleValidation = () => {
    if (!name) {
      ToastUtil.error('Please input Item Name!');
      return false;
    }

    if (type === 'free' && threshold === 0) {
      ToastUtil.error('Please input Item Threshold!');
      return false;
    }

    if (type === 'redeemable' && points === 0) {
      ToastUtil.error('Please input Item Points!');
      return false;
    }

    if (type === 'redeemable' && quantity === 0) {
      ToastUtil.error('Please input Item Quantity!');
      return false;
    }

    if (images.length === 0 && newImages.length === 0) {
      ToastUtil.error('Please add Image to Item!');
      return false;
    }

    if (!instruction) {
      ToastUtil.error('Please add Instruction to redeem Item!');
      return false;
    }

    if (!collectionPoint) {
      ToastUtil.error('Please add Collection Point to Item!');
      return false;
    }

    return true;
  };

  const handleDeactivate = async () => {
    if (
      await AlertService.confirm(
        '',
        'Are you sure you want to deactivate this reward item?'
      )
    ) {
      let api_path = `/reward/edit/${item_id}`;
      let response = await newFetch(POST, api_path, {
        active: false,
      });

      ToastUtil.success(response.message);

      if (response.success) {
        history.push(`/rewards/item/${item_id}`);
      }
    }
  };

  const handleActivate = async () => {
    if (
      await AlertService.confirm(
        '',
        'Are you sure you want to activate this reward item?'
      )
    ) {
      let api_path = `/reward/edit/${item_id}`;
      let response = await newFetch(POST, api_path, {
        active: true,
      });

      ToastUtil.success(response.message);

      if (response.success) {
        history.push(`/rewards/item/${item_id}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      handleValidation() &&
      (await AlertService.confirm(
        '',
        'Are you sure you want to update this item?'
      ))
    ) {
      let api_path = `/reward/edit/${item_id}`;
      let response = await newFetch(POST, api_path, {
        name,
        description: JSON.stringify(
          convertToRaw(description.getCurrentContent())
        ),
        type,
        free_threshold: threshold,
        redeem_points: points,
        quantity,
        mailing_cost: mailingPoints,
        instructions: instruction,
        collection_point: collectionPoint,
        remarks,
      });

      if (response.success) {
        if (newImages.length > 0) {
          let formData = new FormData();
          newImages.map((image) => formData.append('files[]', image));

          let upload_path = `/reward/${item.id}/upload/image`;
          let upload_response = await newFetch(
            POST,
            upload_path,
            formData,
            'form'
          );

          ToastUtil.success(upload_response.message);
        }

        ToastUtil.success(response.message);
        history.push(`/rewards/item/${item_id}`);
      }
    }
  };

  useEffect(() => {
    fetchItem();
  }, []);

  return (
    <LayoutContext.Consumer>
      {({ activeSidebar }) => {
        return (
          <div style={{ paddingTop: '3rem' }}>
            {item && (
              <>
                <div
                  className="d-flex flex-column"
                  style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
                >
                  <NavLink
                    to={`/rewards/item/${item_id}`}
                    className="text-decoration-none"
                    style={{ color: 'inherit' }}
                  >
                    <div style={{ color: '#5156BE' }}>
                      <i className="fas fa-chevron-left pe-2"></i> {item.name}
                    </div>
                  </NavLink>
                  <div className="pt-4">
                    <h5>Edit Item</h5>
                  </div>
                </div>
                <form className="form" onSubmit={(e) => handleSubmit(e)}>
                  <div
                    className="border rounded d-flex"
                    style={{
                      marginTop: '2rem',
                      marginLeft: '3rem',
                      marginRight: '3rem',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '15rem',
                        width: '15rem',
                        backgroundColor: '#f3f3f3',
                        padding: '2.25rem',
                      }}
                      className="border-right"
                    >
                      <strong>Item Information</strong>
                      <p className="pt-2 text-muted">
                        Manage item's information
                      </p>
                    </div>

                    <div
                      className="flex-grow-1 bg-white"
                      style={{ padding: '2.25rem' }}
                    >
                      <div className="row">
                        <div className="col-sm-12">
                          <label htmlFor="itemType">
                            <b>Type of Reward</b>
                          </label>
                          <div className="d-flex">
                            {types.map((t, index) => (
                              <div className="form-check me-3" key={index}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="itemType"
                                  id={`itemType-${index}`}
                                  value={t}
                                  checked={t === type}
                                  onChange={({ target: { value } }) =>
                                    setType(value)
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`itemType-${index}`}
                                >
                                  {`${capitalize(t)} Item`}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-sm-8">
                          <label htmlFor="itemName">
                            <b>Item Name*</b>
                          </label>
                          <input
                            type="text"
                            className="form-control mt-3"
                            placeholder="Item Name"
                            id="itemName"
                            name="itemName"
                            value={name}
                            onChange={({ target: { value } }) => setName(value)}
                          />
                        </div>
                        <div className="col-sm-4">
                          <label htmlFor="itemPoints">
                            <b>Points to Redeem*</b>
                          </label>
                          <input
                            type="number"
                            className="form-control mt-3"
                            id="itemPoints"
                            name="itemPoints"
                            min="0"
                            disabled={type === 'free'}
                            value={points ? points : 0}
                            onChange={({ target: { value } }) =>
                              setPoints(parseInt(value))
                            }
                          />
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-sm-4">
                          <label htmlFor="itemQuantity">
                            <b>Quantity*</b>
                          </label>
                          <input
                            type="number"
                            className="form-control mt-3"
                            id="itemQuantity"
                            name="itemQuantity"
                            min="0"
                            value={quantity ? quantity : 0}
                            onChange={({ target: { value } }) =>
                              setQuantity(parseInt(value))
                            }
                          />
                        </div>
                        <div className="col-sm-4">
                          <label htmlFor="itemThreshold">
                            <b>Item Threshold*</b>
                          </label>
                          <input
                            type="number"
                            className="form-control mt-3"
                            id="itemThreshold"
                            name="itemThreshold"
                            value={threshold ? threshold : 0}
                            min="0"
                            onChange={({ target: { value } }) =>
                              setThreshold(parseInt(value))
                            }
                          />
                        </div>
                        <div className="col-sm-4">
                          <label htmlFor="itemPoints">
                            <b>Mailing Points*</b>
                          </label>
                          <input
                            type="number"
                            className="form-control mt-3"
                            id="itemPoints"
                            name="itemPoints"
                            min="0"
                            value={mailingPoints ? mailingPoints : 0}
                            onChange={({ target: { value } }) =>
                              setMailingPoints(parseInt(value))
                            }
                          />
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-sm-12">
                          <label htmlFor="itemDescription">
                            <b>Item Description</b>
                          </label>
                          <WYSIWYG
                            state={description}
                            setState={setDescription}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="border rounded d-flex"
                    style={{
                      marginTop: '2rem',
                      marginLeft: '3rem',
                      marginRight: '3rem',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '15rem',
                        width: '15rem',
                        backgroundColor: '#f3f3f3',
                        padding: '2.25rem',
                      }}
                      className="border-right"
                    >
                      <strong>Images</strong>
                      <p className="pt-2 text-muted">Manage images of item</p>
                    </div>

                    <div
                      className="flex-grow-1 bg-white"
                      style={{ padding: '2.25rem' }}
                    >
                      {images.length > 0 && (
                        <>
                          <div className="row me-0 ms-0">
                            {images.map((image, index) => (
                              <div
                                className="img-responsive col-3 border rounded pe-0 ps-0 p-2"
                                key={index}
                                style={{
                                  position: 'relative',
                                  background: `url(${`${SERVER_URL}/${image.url}`}) no-repeat center center`,
                                  backgroundSize: 'cover',
                                  height: '200px',
                                }}
                              >
                                <i
                                  className="fas fa-times-circle"
                                  style={{
                                    float: 'right',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                  }}
                                  onClick={() => handleDeleteImage(image.id)}
                                ></i>
                              </div>
                            ))}
                          </div>
                          <hr />
                        </>
                      )}
                      <Upload
                        files={newImages}
                        setFiles={setNewImages}
                        accept="image/*"
                      />
                    </div>
                  </div>
                  <div
                    className="border rounded d-flex"
                    style={{
                      marginTop: '2rem',
                      marginLeft: '3rem',
                      marginRight: '3rem',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '15rem',
                        width: '15rem',
                        backgroundColor: '#f3f3f3',
                        padding: '2.25rem',
                      }}
                      className="border-right"
                    >
                      <strong>Instructions</strong>
                      <p className="pt-2 text-muted">How to redeem item</p>
                    </div>

                    <div
                      className="flex-grow-1 bg-white"
                      style={{ padding: '2.25rem' }}
                    >
                      <textarea
                        className="form-control"
                        rows="5"
                        style={{ resize: 'none' }}
                        placeholder="Item Instruction"
                        id="itemInstruction"
                        name="itemInstruction"
                        value={instruction}
                        onChange={({ target: { value } }) =>
                          setInstruction(value)
                        }
                      />
                    </div>
                  </div>
                  <div
                    className="border rounded d-flex"
                    style={{
                      marginTop: '2rem',
                      marginLeft: '3rem',
                      marginRight: '3rem',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '15rem',
                        width: '15rem',
                        backgroundColor: '#f3f3f3',
                        padding: '2.25rem',
                      }}
                      className="border-right"
                    >
                      <strong>Collection Point</strong>
                      <p className="pt-2 text-muted">Where to collect item</p>
                    </div>
                    <div
                      className="flex-grow-1 bg-white"
                      style={{ padding: '2.25rem' }}
                    >
                      <textarea
                        className="form-control"
                        rows="5"
                        style={{ resize: 'none' }}
                        placeholder="Item Collection Point"
                        id="itemCollectionPoint"
                        name="itemCollectionPoint"
                        value={collectionPoint}
                        onChange={({ target: { value } }) =>
                          setCollectionPoint(value)
                        }
                      />
                    </div>
                  </div>
                  <div
                    className="border rounded d-flex"
                    style={{
                      marginTop: '2rem',
                      marginLeft: '3rem',
                      marginRight: '3rem',
                      marginBottom: '6rem',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '15rem',
                        width: '15rem',
                        backgroundColor: '#f3f3f3',
                        padding: '2.25rem',
                      }}
                      className="border-right"
                    >
                      <strong>Remarks</strong>
                      <p className="pt-2 text-muted">
                        Additional information (optional)
                      </p>
                    </div>
                    <div
                      className="flex-grow-1 bg-white"
                      style={{ padding: '2.25rem' }}
                    >
                      <textarea
                        className="form-control"
                        rows="5"
                        style={{ resize: 'none' }}
                        placeholder="Item Remarks"
                        id="itemRemarks"
                        name="itemRemarks"
                        value={remarks}
                        onChange={({ target: { value } }) => setRemarks(value)}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      position: 'fixed',
                      bottom: 0,
                      width: activeSidebar ? 'calc(100vw - 310px)' : '100vw',
                      height: '4.5rem',
                      paddingRight: '3rem',
                    }}
                    className="border-top bg-white d-flex align-items-center justify-content-between"
                  >
                    <div className="ms-4">
                      <button
                        type="button"
                        className={`btn adjust-fs adjust-shadow ${
                          active ? 'btn-outline-danger' : 'btn-outline-brand'
                        }`}
                        style={{ width: '12rem' }}
                        onClick={active ? handleDeactivate : handleActivate}
                      >
                        {active ? 'Deactivate Item' : 'Activate Item'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger ms-3 adjust-fs adjust-shadow"
                        style={{ width: '12rem' }}
                        onClick={handleDelete}
                      >
                        Delete Item
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-brand adjust-fs adjust-shadow"
                        style={{ width: '12rem' }}
                        onClick={handleDiscard}
                      >
                        Discard changes
                      </button>
                      <button
                        type="submit"
                        className="btn btn-brand ms-3 adjust-fs adjust-shadow"
                        style={{ width: '12rem' }}
                      >
                        Save changes
                      </button>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        );
      }}
    </LayoutContext.Consumer>
  );
};

export default EditItem;
